import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  User, Mail, Phone, MapPin, Bell, Save, Trash2, Plus, Star, Sparkles,
} from 'lucide-react';
import CustomerAccountLayout from '../../components/CustomerAccountLayout';
import PushNotificationsToggle from '../../components/PushNotificationsToggle';
import { US_STATES } from '../../utils/usStates';
import { formatUSPhone } from '../../utils/addressValidation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputBase =
  'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-white placeholder-white/45 text-[14px] focus:outline-none focus:border-yellow-500/50 transition-colors';
const inputError =
  'w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-red-500/60 text-white placeholder-white/45 text-[14px] focus:outline-none focus:border-red-500 transition-colors';

const Toggle = ({ checked, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
      checked ? 'bg-yellow-400' : 'bg-white/15'
    }`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
      checked ? 'translate-x-6' : 'translate-x-1'
    }`} />
  </button>
);

const AccountSettings = () => {
  const token = typeof window !== 'undefined' ? localStorage.getItem('nofee_customer_token') : null;
  // ?welcome=1 is appended after a brand-new signup so we can nudge the user
  // to complete the profile BEFORE landing on a restaurant page.
  const showWelcome =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('welcome') === '1';
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState({
    name: '', phone: '', address: '', zip_code: '', city: '', state: '',
  });
  const [prefs, setPrefs] = useState({
    email_rewards: true, email_referrals: true, email_favorite_alerts: true,
    email_promotions: true, inapp_all: true,
  });
  const [addresses, setAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState({
    label: '', address: '', zip_code: '', city: '', state: '',
  });
  const [saving, setSaving] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});

  const headers = { Authorization: `Bearer ${token}` };

  // Auto-resolve city + state from a 5-digit ZIP — wires both the profile
  // and the "add new address" forms so the customer never has to fight an
  // unfamiliar state abbreviation.
  const resolveZip = async (zip, setter) => {
    if (!/^\d{5}$/.test((zip || '').trim())) return;
    try {
      const r = await axios.get(`${API}/public/zip-lookup/${encodeURIComponent(zip.trim())}`);
      if (r.data?.valid) {
        setter((prev) => ({
          ...prev,
          city: prev.city || r.data.city || '',
          state: prev.state || (r.data.state || '').toUpperCase(),
        }));
      }
    } catch {
      /* soft-fail */
    }
  };

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const [a, b, c] = await Promise.all([
          axios.get(`${API}/customers/me`, { headers }),
          axios.get(`${API}/customer/account/notification-preferences`, { headers }),
          axios.get(`${API}/customer/account/addresses`, { headers }),
        ]);
        const cust = a.data?.customer || {};
        setMe(cust);
        setProfile({
          name: cust.name || '',
          phone: cust.phone || '',
          address: cust.address || '',
          zip_code: cust.zip_code || '',
          city: cust.city || '',
          state: (cust.state || '').toUpperCase(),
        });
        setPrefs(b.data?.preferences || prefs);
        setAddresses(c.data?.addresses || []);
      } catch {
        toast.error('Could not load settings');
      }
    })();
    // eslint-disable-next-line
  }, [token]);

  const saveProfile = async () => {
    // Lightweight validation — the welcome flow expects ALL profile fields
    // to be filled so the checkout has a usable default address. Errors
    // render in red below each empty field (matching the restaurant signup
    // styling the customer just signed off on).
    const errs = {};
    if (!profile.name.trim()) errs.name = 'Required field';
    if (!profile.phone.trim()) errs.phone = 'Required field';
    if (!profile.address.trim()) errs.address = 'Required field';
    if (!/^\d{5}$/.test((profile.zip_code || '').trim())) errs.zip_code = 'Enter a valid 5-digit ZIP';
    if (!profile.city.trim()) errs.city = 'Required field';
    if (!profile.state.trim()) errs.state = 'Select your state';
    if (Object.keys(errs).length > 0) {
      setProfileErrors(errs);
      toast.error('Please complete the highlighted fields');
      return;
    }
    setProfileErrors({});
    setSaving(true);
    try {
      // ── ZIP ↔ City ↔ State cross-validation ────────────────────────────
      // We refuse to persist Kissimmee FL with ZIP 02149 (Boston) or any
      // other contradictory combination. This is the same Zippopotam-backed
      // check the restaurant signup and the checkout flow already use.
      const zipResp = await axios
        .get(`${API}/public/zip-lookup/${encodeURIComponent(profile.zip_code.trim())}`)
        .catch(() => null);
      const expected = zipResp?.data?.valid
        ? { city: (zipResp.data.city || '').trim(), state: (zipResp.data.state || '').toUpperCase() }
        : null;
      if (!expected) {
        setProfileErrors({ zip_code: 'This ZIP code could not be verified' });
        toast.error('We could not verify your ZIP code. Please double-check it.');
        setSaving(false);
        return;
      }
      if (profile.state.toUpperCase() !== expected.state) {
        const err = `ZIP ${profile.zip_code} is in ${expected.state}, but you selected ${profile.state}. Please fix the State or the ZIP code.`;
        setProfileErrors({ state: err, zip_code: ' ' });
        toast.error(err);
        setSaving(false);
        return;
      }
      // Final geocoding — confirms the FULL combination (street + city +
      // state + ZIP) is a real address. Catches "Kissimmee, MA 02149"
      // because Nominatim can't reconcile a Florida city with a
      // Massachusetts ZIP. This is more reliable than hard-coding city
      // names because ZIPs often cover multiple neighborhoods.
      const v = await axios
        .post(`${API}/public/validate-address`, {
          street:   profile.address.trim(),
          city:     profile.city.trim(),
          state:    profile.state.trim(),
          zip_code: profile.zip_code.trim(),
        })
        .catch(() => null);
      if (!v || !v.data?.valid) {
        const err = (v?.data?.error_message
          && `${v.data.error_message} (Expected ${expected.city}, ${expected.state} for ZIP ${profile.zip_code}).`)
          || `That city does not match ZIP ${profile.zip_code}. Expected ${expected.city}, ${expected.state}.`;
        setProfileErrors({ city: err, address: ' ' });
        toast.error(err);
        setSaving(false);
        return;
      }

      await axios.put(`${API}/customer/account/profile`, profile, { headers });
      toast.success('Profile saved');
      if (showWelcome) {
        // Strip ?welcome=1 once the user completes the profile so they
        // don't keep seeing the banner on subsequent visits.
        window.history.replaceState({}, '', '/account/settings');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await axios.put(`${API}/customer/account/notification-preferences`, next, { headers });
    } catch {
      toast.error('Could not save preference');
    }
  };

  const addAddress = async () => {
    if (!newAddr.label.trim() || !newAddr.address.trim()) {
      toast.error('Label and address are required');
      return;
    }
    if (!/^\d{5}$/.test((newAddr.zip_code || '').trim())) {
      toast.error('Please enter a valid 5-digit ZIP code');
      return;
    }
    if (!newAddr.city.trim() || !newAddr.state.trim()) {
      toast.error('Please complete City and State');
      return;
    }
    // ── ZIP ↔ City ↔ State cross-validation (iter92) ─────────────────────
    // Mirrors the profile save so saved addresses can't be saved with
    // contradictory data (e.g. ZIP 02149 + city "Kissimmee" + state FL).
    let expectedCity = '';
    let expectedState = '';
    try {
      const zipResp = await axios.get(`${API}/public/zip-lookup/${encodeURIComponent(newAddr.zip_code.trim())}`);
      if (!zipResp.data?.valid) {
        toast.error('We could not verify this ZIP code.');
        return;
      }
      expectedState = (zipResp.data.state || '').toUpperCase();
      expectedCity = zipResp.data.city || '';
      if (newAddr.state.toUpperCase() !== expectedState) {
        toast.error(`ZIP ${newAddr.zip_code} is in ${expectedState}, not ${newAddr.state}.`);
        return;
      }
    } catch {
      toast.error('Could not verify this ZIP code right now.');
      return;
    }
    // iter91 — geocode the address before saving so the customer can't
    // store fake street names that later break delivery distance calcs.
    try {
      const v = await axios.post(`${API}/public/validate-address`, {
        street:   newAddr.address.trim(),
        city:     newAddr.city.trim(),
        state:    newAddr.state.trim(),
        zip_code: newAddr.zip_code.trim(),
      });
      if (!v.data?.valid) {
        toast.error(`${v.data?.error_message || 'Please correct the address.'} (Expected ${expectedCity}, ${expectedState} for ZIP ${newAddr.zip_code}.)`);
        return;
      }
    } catch {
      // Soft-fail on a Nominatim outage — keep going.
    }
    try {
      const r = await axios.post(`${API}/customer/account/addresses`, { ...newAddr, is_default: addresses.length === 0 }, { headers });
      setAddresses((a) => [...a, r.data.address]);
      setNewAddr({ label: '', address: '', zip_code: '', city: '', state: '' });
      toast.success('Address added');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not add address');
    }
  };

  const removeAddress = async (id) => {
    try {
      await axios.delete(`${API}/customer/account/addresses/${id}`, { headers });
      setAddresses((a) => a.filter((x) => x.id !== id));
      toast.success('Address removed');
    } catch {
      toast.error('Could not remove');
    }
  };

  return (
    <CustomerAccountLayout currentKey="settings" title="Account Settings" subtitle="Profile, addresses, and notification preferences.">
      {!me ? (
        <div className="text-white/45 text-[14px]" data-testid="settings-loading">Loading…</div>
      ) : (
        <div className="space-y-6">
          {showWelcome && (
            <div
              className="p-4 sm:p-5 rounded-2xl border border-yellow-500/40 bg-gradient-to-br from-yellow-500/[0.10] to-yellow-500/[0.02] flex items-start gap-3"
              data-testid="settings-welcome-banner"
            >
              <Sparkles className="w-5 h-5 text-yellow-300 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-[14px] font-bold text-yellow-200">
                  Welcome to NoFee — finish setting up your profile.
                </p>
                <p className="text-[12.5px] text-white/65 mt-0.5">
                  Add your phone, full address and state so we can pre-fill them every time you order.
                  You only do this once.
                </p>
              </div>
            </div>
          )}

          {/* PROFILE */}
          <section className="p-5 sm:p-6 rounded-3xl border border-white/8 bg-white/[0.02]" data-testid="settings-profile">
            <div className="flex items-center gap-2 mb-4">
              <User className="w-4 h-4 text-yellow-300" />
              <h2 className="text-[12.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Profile</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[11.5px] text-white/55 font-medium">Full name</span>
                <input data-testid="settings-name-input"
                  className={`${profileErrors.name ? inputError : inputBase} mt-1`}
                  value={profile.name}
                  onChange={(e) => { setProfile({ ...profile, name: e.target.value }); setProfileErrors((p) => ({ ...p, name: undefined })); }} />
                {profileErrors.name && <p className="mt-1 text-[11.5px] font-semibold text-red-400" data-testid="settings-err-name">{profileErrors.name}</p>}
              </label>
              <label className="block">
                <span className="text-[11.5px] text-white/55 font-medium">Email <span className="text-white/30">· cannot be changed</span></span>
                <div className="mt-1 relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input data-testid="settings-email-readonly" disabled value={me.email || ''} className={`${inputBase} pl-10 opacity-60 cursor-not-allowed`} />
                </div>
              </label>
              <label className="block">
                <span className="text-[11.5px] text-white/55 font-medium">Phone</span>
                <div className="mt-1 relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input data-testid="settings-phone-input"
                    className={`${profileErrors.phone ? inputError : inputBase} pl-10`}
                    value={profile.phone}
                    onChange={(e) => { setProfile({ ...profile, phone: formatUSPhone(e.target.value) }); setProfileErrors((p) => ({ ...p, phone: undefined })); }}
                    placeholder="(617) 555-0123" />
                </div>
                {profileErrors.phone && <p className="mt-1 text-[11.5px] font-semibold text-red-400" data-testid="settings-err-phone">{profileErrors.phone}</p>}
              </label>
              <label className="block">
                <span className="text-[11.5px] text-white/55 font-medium">Street address</span>
                <div className="mt-1 relative">
                  <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <input data-testid="settings-address-input"
                    className={`${profileErrors.address ? inputError : inputBase} pl-10`}
                    value={profile.address}
                    onChange={(e) => { setProfile({ ...profile, address: e.target.value }); setProfileErrors((p) => ({ ...p, address: undefined })); }}
                    placeholder="123 Main Street, Apt 4B" />
                </div>
                {profileErrors.address && <p className="mt-1 text-[11.5px] font-semibold text-red-400" data-testid="settings-err-address">{profileErrors.address}</p>}
              </label>
              <label className="block">
                <span className="text-[11.5px] text-white/55 font-medium">ZIP code</span>
                <input
                  data-testid="settings-zip-input"
                  className={`${profileErrors.zip_code ? inputError : inputBase} mt-1`}
                  value={profile.zip_code}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                    setProfile({ ...profile, zip_code: v });
                    setProfileErrors((p) => ({ ...p, zip_code: undefined }));
                    if (v.length === 5) resolveZip(v, setProfile);
                  }}
                  placeholder="10001"
                  inputMode="numeric"
                  maxLength={5}
                />
                {profileErrors.zip_code && <p className="mt-1 text-[11.5px] font-semibold text-red-400" data-testid="settings-err-zip">{profileErrors.zip_code}</p>}
              </label>
              <label className="block">
                <span className="text-[11.5px] text-white/55 font-medium">City</span>
                <input data-testid="settings-city-input"
                  className={`${profileErrors.city ? inputError : inputBase} mt-1`}
                  value={profile.city}
                  onChange={(e) => { setProfile({ ...profile, city: e.target.value }); setProfileErrors((p) => ({ ...p, city: undefined })); }}
                  placeholder="Boston" />
                {profileErrors.city && <p className="mt-1 text-[11.5px] font-semibold text-red-400" data-testid="settings-err-city">{profileErrors.city}</p>}
              </label>
              <label className="block">
                <span className="text-[11.5px] text-white/55 font-medium">State</span>
                <select
                  data-testid="settings-state-select"
                  className={`${profileErrors.state ? inputError : inputBase} mt-1 appearance-none bg-no-repeat bg-[right_0.75rem_center] bg-[length:14px] pr-9`}
                  style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
                  value={profile.state}
                  onChange={(e) => { setProfile({ ...profile, state: e.target.value }); setProfileErrors((p) => ({ ...p, state: undefined })); }}
                >
                  <option value="" disabled>Select your state</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.code} — {s.name}</option>
                  ))}
                </select>
                {profileErrors.state && <p className="mt-1 text-[11.5px] font-semibold text-red-400" data-testid="settings-err-state">{profileErrors.state}</p>}
              </label>
            </div>
            <button
              type="button"
              onClick={saveProfile}
              disabled={saving}
              data-testid="settings-profile-save"
              className="mt-4 inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[13px] disabled:opacity-60"
            >
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save profile'}
            </button>
          </section>

          {/* ADDRESSES */}
          <section className="p-5 sm:p-6 rounded-3xl border border-white/8 bg-white/[0.02]" data-testid="settings-addresses">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-4 h-4 text-yellow-300" />
              <h2 className="text-[12.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Saved addresses</h2>
            </div>
            {addresses.length === 0 ? (
              <p className="text-[13px] text-white/45">No saved addresses yet.</p>
            ) : (
              <ul className="space-y-2 mb-4">
                {addresses.map((a) => (
                  <li
                    key={a.id}
                    data-testid={`address-row-${a.id}`}
                    className="p-4 rounded-xl border border-white/8 bg-white/[0.015] flex items-start justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[13.5px] font-bold text-white">{a.label}</span>
                        {a.is_default && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-yellow-500/35 bg-yellow-500/10 text-yellow-300 text-[10px] font-bold uppercase tracking-wider">
                            <Star className="w-2.5 h-2.5" /> Default
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-[12.5px] text-white/65 truncate">{a.address}</div>
                      <div className="text-[11.5px] text-white/40">
                        {[a.city, a.state, a.zip_code].filter(Boolean).join(', ')}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAddress(a.id)}
                      data-testid={`address-remove-${a.id}`}
                      className="w-9 h-9 rounded-xl border border-white/10 hover:border-rose-500/40 text-white/55 hover:text-rose-300 inline-flex items-center justify-center transition-all"
                      aria-label="Remove address"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="grid sm:grid-cols-2 gap-2.5">
              <input data-testid="address-label-input" className={inputBase} placeholder="Label (Home, Work)" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} />
              <input data-testid="address-line-input" className={inputBase} placeholder="Street address" value={newAddr.address} onChange={(e) => setNewAddr({ ...newAddr, address: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-3 gap-2.5 mt-2.5">
              <input
                data-testid="address-zip-input"
                className={inputBase}
                placeholder="ZIP"
                value={newAddr.zip_code}
                inputMode="numeric"
                maxLength={5}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setNewAddr({ ...newAddr, zip_code: v });
                  if (v.length === 5) resolveZip(v, setNewAddr);
                }}
              />
              <input data-testid="address-city-input" className={inputBase} placeholder="City" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} />
              <div className="flex gap-2">
                <select
                  data-testid="address-state-select"
                  className={`${inputBase} appearance-none bg-no-repeat bg-[right_0.75rem_center] bg-[length:14px] pr-9 flex-1`}
                  style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")" }}
                  value={newAddr.state}
                  onChange={(e) => setNewAddr({ ...newAddr, state: e.target.value })}
                >
                  <option value="" disabled>State</option>
                  {US_STATES.map((s) => (
                    <option key={s.code} value={s.code}>{s.code}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addAddress}
                  data-testid="address-add-btn"
                  className="px-4 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-extrabold text-[12.5px] inline-flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </section>

          {/* NOTIFICATIONS */}
          <section className="p-5 sm:p-6 rounded-3xl border border-white/8 bg-white/[0.02]" data-testid="settings-notifications">
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-4 h-4 text-yellow-300" />
              <h2 className="text-[12.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Notification preferences</h2>
            </div>

            {/* Web Push toggle (per-device). Renders the in-card variant
                so it sits visually adjacent to the email preference list
                below, but it lives on its own background card so the
                "browser permission" CTA reads as a separate action. */}
            <div className="mb-4">
              <PushNotificationsToggle role="customer" token={token} />
            </div>

            <ul className="space-y-2">
              {[
                { key: 'inapp_all',             label: 'In-app notifications',            desc: 'Show alerts inside the NoFee app.' },
                { key: 'email_favorite_alerts', label: 'Favorite restaurant alerts',      desc: 'New menu items + promotions from places you favorited.' },
                { key: 'email_rewards',         label: 'Rewards & gift credits',          desc: 'Points earned, milestones unlocked, gift credits issued.' },
                { key: 'email_referrals',       label: 'Referral updates',                desc: 'When a friend you invited signs up or places their first order.' },
                { key: 'email_promotions',      label: 'NoFee promotions & news',         desc: 'Occasional emails about new features and seasonal offers.' },
              ].map((p) => (
                <li key={p.key} className="p-4 rounded-xl border border-white/8 bg-white/[0.015] flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-semibold text-white">{p.label}</div>
                    <div className="text-[12px] text-white/55">{p.desc}</div>
                  </div>
                  <Toggle
                    checked={!!prefs[p.key]}
                    onChange={(v) => savePrefs({ ...prefs, [p.key]: v })}
                    testid={`pref-toggle-${p.key}`}
                  />
                </li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </CustomerAccountLayout>
  );
};

export default AccountSettings;
